/**
 * Formats a phone number as the user types.
 * Country code (1-3 digits) is typed first, then remaining digits
 * are grouped in 3s separated by "-".
 * Examples: +256-755-569-649, +1-202-555-0143, +44-20-794-609-58
 */

// Country name (lowercase) → dial code mapping
// Covers all African nations + common global countries
const COUNTRY_DIAL_CODES = {
  // Africa
  'uganda': '256', 'kenya': '254', 'tanzania': '255', 'rwanda': '250',
  'burundi': '257', 'south sudan': '211', 'sudan': '249',
  'democratic republic of congo': '243', 'dr congo': '243', 'drc': '243',
  'congo': '242', 'republic of congo': '242',
  'ethiopia': '251', 'eritrea': '291', 'djibouti': '253', 'somalia': '252',
  'south africa': '27', 'namibia': '264', 'botswana': '267', 'zimbabwe': '263',
  'zambia': '260', 'malawi': '265', 'mozambique': '258', 'lesotho': '266',
  'eswatini': '268', 'swaziland': '268', 'angola': '244', 'madagascar': '261',
  'mauritius': '230', 'seychelles': '248', 'comoros': '269',
  'ghana': '233', 'nigeria': '234', 'cameroon': '237', 'gabon': '241',
  'equatorial guinea': '240', 'sao tome and principe': '239',
  'central african republic': '236', 'chad': '235', 'niger': '227',
  'mali': '223', 'burkina faso': '226', 'senegal': '221', 'gambia': '220',
  'guinea': '224', 'guinea-bissau': '245', 'sierra leone': '232',
  'liberia': '231', "cote d'ivoire": '225', "côte d'ivoire": '225',
  'ivory coast': '225', 'togo': '228', 'benin': '229',
  'cape verde': '238', 'cabo verde': '238', 'morocco': '212',
  'algeria': '213', 'tunisia': '216', 'libya': '218', 'egypt': '20',
  'mauritania': '222',
  // Common global
  'united states': '1', 'usa': '1', 'united states of america': '1',
  'canada': '1', 'united kingdom': '44', 'uk': '44', 'britain': '44',
  'ireland': '353', 'france': '33', 'germany': '49', 'spain': '34',
  'italy': '39', 'portugal': '351', 'netherlands': '31', 'belgium': '32',
  'switzerland': '41', 'austria': '43', 'sweden': '46', 'norway': '47',
  'denmark': '45', 'finland': '358', 'iceland': '354',
  'australia': '61', 'new zealand': '64', 'india': '91', 'pakistan': '92',
  'bangladesh': '880', 'china': '86', 'japan': '81', 'south korea': '82',
  'korea': '82', 'singapore': '65', 'malaysia': '60', 'thailand': '66',
  'philippines': '63', 'indonesia': '62', 'vietnam': '84',
  'saudi arabia': '966', 'uae': '971', 'united arab emirates': '971',
  'qatar': '974', 'kuwait': '965', 'bahrain': '973', 'oman': '968',
  'jordan': '962', 'lebanon': '961', 'israel': '972', 'turkey': '90',
  'iran': '98', 'iraq': '964',
  'brazil': '55', 'argentina': '54', 'mexico': '52', 'colombia': '57',
  'peru': '51', 'chile': '56', 'venezuela': '58', 'ecuador': '593',
  'bolivia': '591', 'paraguay': '595', 'uruguay': '598', 'cuba': '53',
  'jamaica': '1', 'trinidad and tobago': '1', 'barbados': '1',
  'russia': '7', 'kazakhstan': '7', 'ukraine': '380', 'poland': '48',
  'czech republic': '420', 'czechia': '420', 'slovakia': '421',
  'hungary': '36', 'romania': '40', 'bulgaria': '359', 'greece': '30',
  'croatia': '385', 'serbia': '381', 'slovenia': '386',
  'bosnia and herzegovina': '387', 'albania': '355', 'macedonia': '389',
  'north macedonia': '389', 'montenegro': '382', 'kosovo': '383',
  'belarus': '375', 'moldova': '373', 'georgia': '995', 'armenia': '374',
  'azerbaijan': '994',
};

/**
 * Returns the dial code for a given country name.
 * @param {string} countryName - e.g. "Uganda", "Kenya", "United Kingdom"
 * @returns {string|null} - e.g. "256", "254", "44", or null if unknown
 */
export function getDialCodeForCountry(countryName) {
  if (!countryName) return null;
  const key = countryName.trim().toLowerCase();
  return COUNTRY_DIAL_CODES[key] || null;
}

/**
 * Returns a formatted dial code prefix, e.g. "+256-"
 * @param {string} countryName
 * @returns {string} e.g. "+256-" or "+256" if no country matched
 */
export function getDialCodePrefix(countryName) {
  const code = getDialCodeForCountry(countryName);
  if (!code) return '';
  return '+' + code + '-';
}

/**
 * Applies a country's dial code to a phone value if it doesn't already start with a +.
 * If the value is empty, returns just the dial code prefix.
 * If the value already has a +, it's reformatted with the country's dial code length.
 * @param {string} value - current phone input value
 * @param {string} countryName - country name from the form
 * @returns {string} formatted phone with country dial code applied
 */
export function applyDialCodeToPhone(value, countryName) {
  const dialCode = getDialCodeForCountry(countryName);
  if (!dialCode) return formatPhoneNumber(value);

  if (!value || value.trim() === '') {
    return '';
  }

  // Strip everything to digits and +
  let cleaned = value.replace(/[^\d+]/g, '');
  if (!cleaned.startsWith('+')) {
    // If the number already starts with the dial code digits (without +), prepend +
    cleaned = '+' + cleaned;
  }

  const digits = cleaned.slice(1);

  // If the user has typed nothing beyond the country code, return empty
  if (digits.length === 0) return '';

  // If the existing digits start with the dial code, use it directly
  if (digits.startsWith(dialCode)) {
    const rest = digits.slice(dialCode.length);
    if (rest.length === 0) return '';
    const groups = rest.match(/.{1,3}/g) || [];
    return '+' + dialCode + '-' + groups.join('-');
  }

  // If the number starts with a 0 (local format), strip it and prepend dial code
  if (digits.startsWith('0')) {
    const rest = digits.slice(1);
    if (rest.length === 0) return '';
    const groups = rest.match(/.{1,3}/g) || [];
    return '+' + dialCode + '-' + groups.join('-');
  }

  // If the number starts with a different country code, reformat with the new one
  // (user changed country — replace the old dial code with the new one)
  // Detect old code length
  const oldCcLen = detectCountryCodeLength(digits);
  const rest = digits.slice(oldCcLen);
  if (rest.length === 0) return '';
  const groups = rest.match(/.{1,3}/g) || [];
  return '+' + dialCode + '-' + groups.join('-');
}

// All valid 3-digit country codes (ITU-T E.164)
const THREE_DIGIT_CODES = new Set([
  '211','212','213','214','215','216','218','220','221','222','223','224','225',
  '226','227','228','229','230','231','232','233','234','235','236','237','238',
  '239','240','241','242','243','244','245','246','247','248','249','250','251',
  '252','253','254','255','256','257','258','259','260','261','262','263','264',
  '265','266','267','268','269','290','291','297','298','299','350','351','352',
  '353','354','355','356','357','358','359','370','371','372','373','374','375',
  '376','377','378','380','381','382','383','385','386','387','389','420','421',
  '423','500','501','502','503','504','505','506','507','508','509','590','591',
  '592','593','594','595','596','597','598','599','670','672','673','674','675',
  '676','677','678','679','680','681','682','683','685','686','687','688','689',
  '690','691','692','850','852','853','855','856','870','878','880','881','882',
  '883','886','888','960','961','962','963','964','965','966','967','968','970',
  '971','972','973','974','975','976','977','979','992','993','994','995','996','998',
]);

// All valid 2-digit country codes
const TWO_DIGIT_CODES = new Set([
  '20','27','30','31','32','33','34','36','39','40','41','43','44','45','46','48',
  '49','51','52','53','54','55','56','57','58','60','61','62','63','64','65','66',
  '81','82','84','86','90','91','92','93','94','95','98',
]);

/**
 * Detects the country code length from the digit string.
 * Returns 1, 2, or 3 based on known ITU-T E.164 assignments.
 */
function detectCountryCodeLength(digits) {
  if (digits.length === 0) return 0;

  const first = digits[0];

  // 1 and 7 are the only 1-digit country codes
  if (first === '1' || first === '7') return 1;

  // Try 3-digit match first (longest prefix wins)
  if (digits.length >= 3 && THREE_DIGIT_CODES.has(digits.slice(0, 3))) return 3;

  // Try 2-digit match
  if (digits.length >= 2 && TWO_DIGIT_CODES.has(digits.slice(0, 2))) return 2;

  // Fallback: if 3+ digits typed and no match, assume 3-digit code
  if (digits.length >= 3) return 3;
  // If 2 digits typed and no match yet, could be a 2 or 3 digit code — wait for more
  if (digits.length === 2) return 2;
  // 1 digit that's not 1 or 7 — wait for more
  return 1;
}

export function formatPhoneNumber(value) {
  if (!value) return '';

  let cleaned = value.replace(/[^\d+]/g, '');

  if (!cleaned.startsWith('+')) {
    cleaned = '+' + cleaned;
  }

  const digits = cleaned.slice(1);

  if (digits.length === 0) return '+';

  const ccLen = detectCountryCodeLength(digits);

  // While the country code is still ambiguous (not enough digits typed),
  // just show what we have without dashes
  if (digits.length <= ccLen) {
    return '+' + digits;
  }

  const countryCode = digits.slice(0, ccLen);
  const rest = digits.slice(ccLen);
  const groups = rest.match(/.{1,3}/g) || [];
  return '+' + countryCode + '-' + groups.join('-');
}

// Sorted list of country names (display-friendly) for datalist options
export const COUNTRY_NAMES = Object.keys(COUNTRY_DIAL_CODES)
  .map(key => key.replace(/\b\w/g, c => c.toUpperCase()))
  .filter((v, i, arr) => arr.indexOf(v) === i)
  .sort((a, b) => a.localeCompare(b));
